/**
 * useAlunosTurma Hook v2.0
 * 
 * 🚨 REFATORADO PARA OFFLINE-FIRST
 * 
 * ANTES: Chamava supabase diretamente (4 queries)
 * AGORA: Usa dataProvider (IndexedDB primeiro, fallback Supabase)
 * 
 * 🚫 DO NOT ACCESS SUPABASE HERE - Use dataProvider only
 */

import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAlunosByTurma,
  getTurmaById,
  getPresencasByTurma
} from "@/lib/dataProvider";

export type NivelAtencaoFrequencia = "critico" | "atencao" | "regular" | "sem_dados";

export interface AlunoTurmaResumo {
  id: string;
  nome: string;
  matricula: string;
  faltas: number;
  frequencia: number;
  totalChamadas: number;
  faltouHoje: boolean;
  faltaJustificadaHoje: boolean;
  faltasConsecutivas: number;
  ultimaFalta: string | null;
  nivelAtencao: NivelAtencaoFrequencia;
  turma_id: string;
  nome_responsavel?: string;
  telefone_responsavel?: string;
  data_nascimento?: string;
  endereco?: string;
  [key: string]: any; // Allow additional fields
}

interface TurmaInfo {
  id: string;
  nome: string;
  numero_sala: string;
  totalAlunos?: number;
  alunosFaltosos?: number;
  alunosPresentes?: number;
  alunosEmAtencao?: number;
  percentualPresencaHoje?: number;
}

export function useAlunosTurma(turmaId?: string, campos: string[] = ["id", "nome", "matricula", "turma_id"]) {
  const { user } = useAuth();
  const [alunos, setAlunos] = useState<AlunoTurmaResumo[]>([]);
  const [turmaInfo, setTurmaInfo] = useState<TurmaInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const fetchAlunos = useCallback(async () => {
    if (!turmaId) return;

    setLoading(true);

    try {
      // 1. Get turma info via dataProvider (OFFLINE-FIRST)
      const turmaResult = await getTurmaById(turmaId);

      if (!turmaResult.data) {
        console.warn('[useAlunosTurma] Turma not found in cache or network');
        setLoading(false);
        return;
      }

      const turmaData = turmaResult.data;

      // 2. Get alunos via dataProvider (OFFLINE-FIRST)
      const alunosResult = await getAlunosByTurma(turmaId, user?.escola_id);
      const alunosData = alunosResult.data;

      // 3. Carrega somente a janela recente usada nesta tela.
      // O histórico completo continua disponível nas telas de relatório/perfil.
      let presencasData: any[] = [];
      let presencasHojeData: any[] = [];

      try {
        const inicioJanela = new Date();
        inicioJanela.setDate(inicioJanela.getDate() - 30);
        const desde = format(inicioJanela, 'yyyy-MM-dd');
        const presencasResult = await getPresencasByTurma(turmaId, desde);
        presencasData = presencasResult.data;

        // Filter today's presencas
        const hoje = format(new Date(), 'yyyy-MM-dd');
        presencasHojeData = presencasData.filter((p: any) => p.data_chamada === hoje);
      } catch (err) {
        console.warn('Error fetching presencas:', err);
      }

      // 4. Resume frequência e dias de ausência por aluno.
      const statsMap = new Map<string, {
        total: number;
        faltas: number;
        faltasJustificadas: number;
        dias: Map<string, { faltaNaoJustificada: boolean }>;
      }>();

      presencasData.forEach((p: any) => {
        const current = statsMap.get(p.aluno_id) || {
          total: 0,
          faltas: 0,
          faltasJustificadas: 0,
          dias: new Map<string, { faltaNaoJustificada: boolean }>(),
        };

        current.total++;
        if (!p.presente && !p.falta_justificada) {
          current.faltas++;
        }
        if (!p.presente && p.falta_justificada) {
          current.faltasJustificadas++;
        }

        const dia = current.dias.get(p.data_chamada) || { faltaNaoJustificada: false };
        dia.faltaNaoJustificada = dia.faltaNaoJustificada || (!p.presente && !p.falta_justificada);
        current.dias.set(p.data_chamada, dia);
        statsMap.set(p.aluno_id, current);
      });

      // 5. Calculate today's stats
      const alunosComFaltaHoje = new Set(
        presencasHojeData
          .filter(p => !p.presente && !p.falta_justificada)
          .map(p => p.aluno_id),
      );
      const alunosPresentesHoje = new Set(
        presencasHojeData
          .filter(p => p.presente)
          .map(p => p.aluno_id),
      );
      const faltososHoje = alunosComFaltaHoje.size;
      const presentesHoje = alunosPresentesHoje.size;
      const totalRegistrosHoje = new Set(presencasHojeData.map(p => p.aluno_id)).size;
      const percentualPresencaHoje = totalRegistrosHoje > 0
        ? Math.round((presentesHoje / totalRegistrosHoje) * 100)
        : undefined;

      // 6. Processa indicadores individuais da janela de 30 dias.
      const processedAlunos = alunosData.map((aluno) => {
        const stats = statsMap.get(aluno.id) || {
          total: 0,
          faltas: 0,
          faltasJustificadas: 0,
          dias: new Map<string, { faltaNaoJustificada: boolean }>(),
        };
        const frequencia = stats.total > 0
          ? Math.round(((stats.total - stats.faltas) / stats.total) * 100)
          : 100;
        const diasOrdenados = [...stats.dias.entries()]
          .sort(([dataA], [dataB]) => dataB.localeCompare(dataA));
        let faltasConsecutivas = 0;

        for (const [, dia] of diasOrdenados) {
          if (!dia.faltaNaoJustificada) break;
          faltasConsecutivas++;
        }

        const registroHoje = presencasHojeData.filter(p => p.aluno_id === aluno.id);
        const faltouHoje = registroHoje.some(p => !p.presente && !p.falta_justificada);
        const faltaJustificadaHoje =
          !faltouHoje && registroHoje.some(p => !p.presente && p.falta_justificada);
        const ultimaFalta =
          diasOrdenados.find(([, dia]) => dia.faltaNaoJustificada)?.[0] || null;
        const nivelAtencao: NivelAtencaoFrequencia =
          stats.total === 0
            ? "sem_dados"
            : faltouHoje || faltasConsecutivas >= 3 || frequencia < 75
              ? "critico"
              : faltasConsecutivas >= 2 || frequencia < 85
                ? "atencao"
                : "regular";

        return {
          ...aluno,
          faltas: stats.faltas,
          frequencia: Math.max(0, Math.min(100, frequencia)),
          totalChamadas: stats.total,
          faltouHoje,
          faltaJustificadaHoje,
          faltasConsecutivas,
          ultimaFalta,
          nivelAtencao,
        };
      });

      // 7. Update state
      setTurmaInfo({
        id: turmaData.id,
        nome: turmaData.nome,
        numero_sala: turmaData.numero_sala || '',
        totalAlunos: alunosData.length,
        alunosFaltosos: faltososHoje,
        alunosPresentes: presentesHoje,
        alunosEmAtencao: processedAlunos.filter(
          aluno => aluno.nivelAtencao === "critico" || aluno.nivelAtencao === "atencao",
        ).length,
        percentualPresencaHoje
      });

      setAlunos(processedAlunos.sort((a, b) => a.nome.localeCompare(b.nome)));

      // Log source for debugging
      console.log('[useAlunosTurma] Data loaded', {
        turmaSource: turmaResult.source,
        alunosSource: alunosResult.source,
        alunosCount: alunosData.length,
        isOffline: !navigator.onLine
      });

    } catch (error) {
      console.error("[useAlunosTurma] Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [turmaId, user?.escola_id]);

  // Listen for online/offline changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      fetchAlunos(); // Refresh when coming online
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchAlunos]);

  // Initial fetch
  useEffect(() => {
    fetchAlunos();
  }, [fetchAlunos]);

  const refreshAlunos = useCallback(() => {
    fetchAlunos();
  }, [fetchAlunos]);

  return {
    alunos,
    setAlunos,
    turmaInfo,
    loading,
    refreshAlunos,
    isOffline // Expose offline status for UI
  };
}
